import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';
import { FormattedVacancy } from 'utils/fetch-format-vacancy';
import BaseButton from 'components/base-button';

import styles from './vacancy-detail-text.module.scss';

export type VacancyDetailTextProps = {
	vacancy: FormattedVacancy;
	border?: boolean;
	compact?: boolean;
};

const VacancyDetailText = ({ vacancy, border, compact }: VacancyDetailTextProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { description, engagement, deadline, applyUrl, location, department, metaDetails } = vacancy || {};

	const parsedHtml = description ? sanitizeAndParseHtml(description) : undefined;
	const fallbackText = formatMessage({ id: 'modules.vacancies.unknown' });

	if (!vacancy) return <></>;

	return (
		<div className={clsx(styles.row, styles.rowWithSidebar, border && styles.border)}>
			<section className={styles.content}>{parsedHtml}</section>
			<aside className={styles.sidebar}>
				<div className={styles.blueBox}>
					<div className={styles.title}>{formatMessage({ id: 'modules.vacancies.info' })}</div>

					<div className={clsx('dark', styles.cardBody)}>
						{!!department?.Name && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.department' })}:</strong>
								<span>{department?.Name || fallbackText}</span>
							</div>
						)}
						{!!metaDetails?.salaryScale && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.salaryScale' })}:</strong>
								<span>{metaDetails.salaryScale || fallbackText}</span>
							</div>
						)}
						{!!metaDetails?.salary && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.salary' })}:</strong>
								<span>{metaDetails.salary || fallbackText}</span>
							</div>
						)}
						{!!metaDetails?.category && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.category' })}:</strong>
								<span>{metaDetails.category || fallbackText}</span>
							</div>
						)}
						{!!metaDetails?.role && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.role' })}:</strong>
								<span>{metaDetails.role || fallbackText}</span>
							</div>
						)}
						{!!metaDetails?.hours && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.hours' })}:</strong>
								<span>{metaDetails.hours || fallbackText}</span>
							</div>
						)}

						{!!engagement && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.engagement' })}:</strong>
								<span>{engagement || fallbackText}</span>
							</div>
						)}

						{!!deadline && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.deadline' })}:</strong>
								<span>{deadline || fallbackText}</span>
							</div>
						)}

						{!!location && !compact && (
							<div>
								<strong>{formatMessage({ id: 'modules.vacancies.location' })}:</strong>
								<span>{location || fallbackText}</span>
							</div>
						)}
					</div>

					<div className={styles.actions}>
						{!!department?.HomepageURL && !compact && (
							<BaseButton
								as="a"
								colorScheme="tertiary"
								variant="outlined"
								href={department?.HomepageURL}
								target="_blank"
								fullWidth
							>
								{formatMessage({ id: 'modules.vacancies.departmentPage' })}
							</BaseButton>
						)}

						{applyUrl && (
							<BaseButton as="a" colorScheme="secondary" variant="filled" href={applyUrl} target="_blank" fullWidth>
								{formatMessage({ id: 'modules.vacancies.apply' })}
							</BaseButton>
						)}
					</div>
				</div>
			</aside>
		</div>
	);
};

export default VacancyDetailText;
